import type { Order, PortfolioResponse, Position, Quote, Transaction } from '../api/types'

const TERMINAL_ORDER_STATUSES = new Set([
  'FILLED',
  'CANCELLED',
  'CANCELED',
  'REJECTED',
  'EXPIRED',
])

const OCC_RE = /^([A-Z]+)\d{6}[CP]\d{8}$/

export function underlyingOf(symbol: string): string {
  const m = symbol.match(OCC_RE)
  return m ? m[1] : symbol
}

export function isOptionSymbol(symbol: string): boolean {
  return OCC_RE.test(symbol)
}

export interface ActivePosition {
  kind: 'POSITION'
  position: Position
  /** Live equity price from the quote stream, if available. Undefined for options or when quote is missing. */
  livePrice?: number
  /** Computed unrealized P/L in dollars. Live for equity, fallback to costBasis.gainValue otherwise. */
  unrealizedPL: number
  /** Same as above, as a percentage of cost. */
  unrealizedPLPct: number
  /** Day percent change, from quote if available, else from positionDailyGain. */
  dayPct: number
  /** Quote object for surfacing volume / previousClose etc. */
  quote?: Quote
  /** Trades for this position's symbol since the position opened, newest first. */
  transactions: Transaction[]
}

export interface ActivePendingOrder {
  kind: 'PENDING'
  order: Order
  /** Live equity price from the quote stream, if available. */
  livePrice?: number
  /** Signed distance live − limitPrice (or stopPrice). Undefined when both prices are missing. */
  distance?: number
  distancePct?: number
  quote?: Quote
}

export interface ActiveClosedPosition {
  kind: 'CLOSED'
  symbol: string
  instrumentType: 'EQUITY' | 'OPTION' | string
  /** Sum of netAmount across today's TRADE transactions for this symbol. */
  realizedPL: number
  /**
   * realizedPL as a percentage of the capital deployed on the opening side of
   * the cycle (cost basis for a long round-trip; proceeds for a short cover).
   * Undefined when cost basis can't be derived.
   */
  realizedPLPct?: number
  /** Today's trades for this symbol, newest first. */
  transactions: Transaction[]
  /** Most recent trade timestamp (used for sorting). */
  closedAt: string
}

export type ActiveTradeRow = ActivePosition | ActivePendingOrder | ActiveClosedPosition

export interface ActiveTradesView {
  pending: ActivePendingOrder[]
  positions: ActivePosition[]
  closed: ActiveClosedPosition[]
  activeSymbols: string[]
}

export interface JoinInput {
  upstreamOrderIds: Set<string>
  appSymbols: Set<string>
  portfolio: PortfolioResponse | undefined
  quotesBySymbol: Map<string, Quote>
  transactionsBySymbol?: Map<string, Transaction[]>
  /** Local-time day being viewed. Omit for today. */
  viewDate?: Date
}

export function isTerminalOrderStatus(status: string | undefined): boolean {
  if (!status) return false
  return TERMINAL_ORDER_STATUSES.has(status.toUpperCase())
}

/**
 * Capital deployed on the opening side of a round-trip cycle, used as the
 * denominator for realized-PL percentage. Opening side is the first fill's
 * side (BUY for a long round-trip, SELL for a short). Sums |netAmount| —
 * cash-true and already inclusive of fees, matching how realizedPL is summed.
 */
function cycleCostBasis(cycle: Transaction[]): number {
  if (cycle.length === 0) return 0
  const openSide = cycle[0].side
  if (openSide !== 'BUY' && openSide !== 'SELL') return 0
  let cost = 0
  for (const t of cycle) {
    if (t.side === openSide) cost += Math.abs(t.netAmount ?? 0)
  }
  return cost
}

/**
 * Split a chronologically-ascending list of TRADE transactions for a single
 * symbol into round-trip cycles. A cycle ends when running net quantity
 * returns to zero (signed: BUY adds, SELL subtracts). Handles long, short,
 * and partial-scale flows identically — the only thing that matters is when
 * the position goes flat.
 *
 * Trailing trades that don't bring net qty back to zero (still-open position
 * at the end of the day) are discarded; the open-position card handles those.
 */
export function splitRoundTripCycles(tradesAsc: Transaction[]): Transaction[][] {
  const cycles: Transaction[][] = []
  let current: Transaction[] = []
  let runningQty = 0
  for (const t of tradesAsc) {
    current.push(t)
    const q = t.quantity ?? 0
    if (t.side === 'BUY') runningQty += q
    else if (t.side === 'SELL') runningQty -= q
    if (runningQty === 0 && current.length > 0) {
      cycles.push(current)
      current = []
    }
  }
  return cycles
}

/**
 * Collapse multiple fills that occurred within the same second AND share
 * side + unitPrice + orderType + kind into a single synthetic transaction.
 * Quantities, netAmounts, fees, and rebates sum. Other fields take the first
 * entry's value. Useful for display: brokers often split a single order into
 * many micro-fills printed in the same second, which are noise to the user.
 *
 * Preserves the input order: the first occurrence of each group's key keeps
 * its position in the returned array. Inputs of length 0 or 1 are returned
 * as-is.
 */
export function groupSameSecondFills(transactions: Transaction[]): Transaction[] {
  if (transactions.length <= 1) return transactions
  const groups = new Map<string, Transaction[]>()
  for (const t of transactions) {
    const sec = Math.floor(Date.parse(t.timestamp) / 1000)
    const key = [
      sec,
      t.kind,
      t.side ?? '',
      t.unitPrice ?? '',
      t.orderType ?? '',
    ].join('|')
    const arr = groups.get(key) ?? []
    arr.push(t)
    groups.set(key, arr)
  }
  const result: Transaction[] = []
  for (const arr of groups.values()) {
    if (arr.length === 1) {
      result.push(arr[0])
      continue
    }
    const first = arr[0]
    const totalQty = arr.reduce((sum, t) => sum + (t.quantity ?? 0), 0)
    const totalNet = arr.reduce((sum, t) => sum + t.netAmount, 0)
    const hasGross = arr.some((t) => t.grossAmount != null)
    const totalGross = arr.reduce((sum, t) => sum + (t.grossAmount ?? 0), 0)
    const totalFees = arr.reduce((sum, t) => sum + t.fees, 0)
    const totalRebates = arr.reduce((sum, t) => sum + t.rebates, 0)
    result.push({
      ...first,
      id: arr.map((t) => t.id).join('|'),
      quantity: totalQty,
      netAmount: totalNet,
      grossAmount: hasGross ? totalGross : undefined,
      fees: totalFees,
      rebates: totalRebates,
    })
  }
  return result
}

function num(value: string | undefined): number {
  if (!value) return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function positionSymbolMatchesAppSet(position: Position, appSymbols: Set<string>): boolean {
  const sym = position.instrument.symbol
  if (appSymbols.has(sym)) return true
  // Option positions may have been opened with a slightly different OCC-format
  // representation; match by underlying as a fallback for equity-only sets.
  if (isOptionSymbol(sym) && appSymbols.has(underlyingOf(sym))) return true
  return false
}

function dayWindow(date: Date): { startIso: string; endIso: string } {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { startIso: start.toISOString(), endIso: end.toISOString() }
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

export function joinActiveTrades(input: JoinInput): ActiveTradesView {
  const { upstreamOrderIds, appSymbols, portfolio, quotesBySymbol } = input
  const transactionsBySymbol = input.transactionsBySymbol ?? new Map<string, Transaction[]>()
  const viewingToday = !input.viewDate || isSameLocalDay(input.viewDate, new Date())
  const window = dayWindow(input.viewDate ?? new Date())

  if (!viewingToday) {
    // Historical view: only show closed positions for that day. No open
    // positions, no working orders, no live quotes needed.
    const closed: ActiveClosedPosition[] = []
    for (const symbol of appSymbols) {
      const txAll = transactionsBySymbol.get(symbol) ?? []
      const dayTradesAsc = txAll
        .filter(
          (t) =>
            t.kind === 'TRADE' && t.timestamp >= window.startIso && t.timestamp < window.endIso,
        )
        .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      if (dayTradesAsc.length === 0) continue
      const cycles = splitRoundTripCycles(dayTradesAsc)
      const instrumentType =
        dayTradesAsc[0]?.instrumentType ?? (isOptionSymbol(symbol) ? 'OPTION' : 'EQUITY')
      for (const cycle of cycles) {
        const realizedPL = cycle.reduce((sum, t) => sum + (t.netAmount ?? 0), 0)
        const cost = cycleCostBasis(cycle)
        closed.push({
          kind: 'CLOSED',
          symbol,
          instrumentType,
          realizedPL,
          realizedPLPct: cost > 0 ? (realizedPL / cost) * 100 : undefined,
          transactions: [...cycle].reverse(), // newest-first for display
          closedAt: cycle[cycle.length - 1].timestamp,
        })
      }
    }
    closed.sort((a, b) => b.closedAt.localeCompare(a.closedAt))
    return { pending: [], positions: [], closed, activeSymbols: [] }
  }

  if (!portfolio) {
    return { pending: [], positions: [], closed: [], activeSymbols: [] }
  }
  const todayStart = window.startIso

  const pending: ActivePendingOrder[] = []
  for (const order of portfolio.orders ?? []) {
    if (!upstreamOrderIds.has(order.orderId)) continue
    if (isTerminalOrderStatus(order.status) || order.closedAt) continue
    const quote = quotesBySymbol.get(order.instrument.symbol)
    const live = quote ? Number(quote.last) : NaN
    const reference = num(order.limitPrice) || num(order.stopPrice)
    const livePrice = Number.isFinite(live) ? live : undefined
    const distance =
      livePrice != null && reference > 0 ? livePrice - reference : undefined
    const distancePct =
      distance != null && reference > 0 ? (distance / reference) * 100 : undefined
    pending.push({ kind: 'PENDING', order, livePrice, distance, distancePct, quote })
  }

  const positions: ActivePosition[] = []
  for (const position of portfolio.positions ?? []) {
    if (!positionSymbolMatchesAppSet(position, appSymbols)) continue
    if (num(position.quantity) === 0) continue

    const quote = quotesBySymbol.get(position.instrument.symbol)
    const live = quote ? Number(quote.last) : NaN
    const livePrice = Number.isFinite(live) ? live : undefined

    const qty = num(position.quantity)
    const unitCost = num(position.costBasis?.unitCost)
    const isOption = position.instrument.type === 'OPTION'
    const multiplier = isOption ? 100 : 1

    let unrealizedPL: number
    if (livePrice != null && unitCost > 0) {
      unrealizedPL = (livePrice - unitCost) * qty * multiplier
    } else {
      unrealizedPL = num(position.costBasis?.gainValue)
    }
    const totalCost = num(position.costBasis?.totalCost)
    const unrealizedPLPct =
      totalCost > 0 ? (unrealizedPL / totalCost) * 100 : num(position.costBasis?.gainPercentage)

    let dayPct = num(position.positionDailyGain?.gainPercentage)
    if (quote?.oneDayChange?.percentChange) {
      dayPct = num(quote.oneDayChange.percentChange)
    }

    const openedAt = position.openedAt ?? ''
    const txAll = transactionsBySymbol.get(position.instrument.symbol) ?? []
    const transactions = txAll
      .filter((t) => t.kind === 'TRADE' && (!openedAt || t.timestamp >= openedAt))
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))

    positions.push({
      kind: 'POSITION',
      position,
      livePrice,
      unrealizedPL,
      unrealizedPLPct,
      dayPct,
      quote,
      transactions,
    })
  }

  const openSymbols = new Set(positions.map((p) => p.position.instrument.symbol))
  const closed: ActiveClosedPosition[] = []
  for (const symbol of appSymbols) {
    if (openSymbols.has(symbol)) continue
    const txAll = transactionsBySymbol.get(symbol) ?? []
    const todayTradesAsc = txAll
      .filter((t) => t.kind === 'TRADE' && t.timestamp >= todayStart)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    if (todayTradesAsc.length === 0) continue
    const cycles = splitRoundTripCycles(todayTradesAsc)
    const instrumentType =
      todayTradesAsc[0]?.instrumentType ?? (isOptionSymbol(symbol) ? 'OPTION' : 'EQUITY')
    for (const cycle of cycles) {
      const realizedPL = cycle.reduce((sum, t) => sum + (t.netAmount ?? 0), 0)
      const cost = cycleCostBasis(cycle)
      closed.push({
        kind: 'CLOSED',
        symbol,
        instrumentType,
        realizedPL,
        realizedPLPct: cost > 0 ? (realizedPL / cost) * 100 : undefined,
        transactions: [...cycle].reverse(), // newest-first for display
        closedAt: cycle[cycle.length - 1].timestamp,
      })
    }
  }
  closed.sort((a, b) => b.closedAt.localeCompare(a.closedAt))

  const symbolSet = new Set<string>()
  for (const p of positions) symbolSet.add(p.position.instrument.symbol)
  for (const o of pending) symbolSet.add(o.order.instrument.symbol)
  const activeSymbols = [...symbolSet].sort()

  return { pending, positions, closed, activeSymbols }
}
